import { createElement, useEffect, useRef, useState, ReactNode, type JSX } from "react";
import { useInteractionReady } from "./InteractionGate";
import { canMountDeferredContent } from "@/lib/engagement";

interface LazySectionProps {
  children: ReactNode;
  /** How much of the previous section should be visible before this one loads (0-1) */
  rootMargin?: string;
  /** Fallback while not yet visible */
  fallback?: ReactNode;
  /** HTML tag for the wrapper */
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Wraps a section so its children only mount once the element enters
 * (or is about to enter) the viewport. Uses IntersectionObserver for
 * zero-JS-scroll-listener perf. SEO-safe: renders a real DOM node
 * with optional min-height placeholder; no overflow/scroll hacks.
 */
export default function LazySection({
  children,
  rootMargin = "100px 0px",
  fallback = null,
  as: Tag = "div",
  className,
  style,
}: LazySectionProps) {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const armed = useInteractionReady();

  useEffect(() => {
    if (!armed) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, armed]);

  return createElement(Tag as any, { ref, className, style, "data-lazy-section": visible ? "loaded" : "deferred" }, canMountDeferredContent(armed, visible) ? children : fallback);
}
